import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/base/select";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { useStateMachine } from "@/core/arch/state_machine";
import { ActivationBonusBloc } from "@/application/marketing/activation-bonus/activation_bonus_bloc";
import { ActivationBonusRepository } from "@/application/marketing/activation-bonus/activation_bonus_repository";
import {
  isCreatedRangeInvalid,
  type ActivationBonusStage,
  type ActivationBonusCampaignRow,
  type CreatedRangePreset,
} from "@/application/marketing/activation-bonus/types";
import { StageBadge } from "@/presentation/merchants/components/StageBadge";

const STAGE_OPTIONS: {
  value: ActivationBonusStage;
  label: string;
  hint: string;
}[] = [
  {
    value: "eligible",
    label: "Eligible",
    hint: "Enrolled, awaiting a qualifying sale",
  },
  { value: "paid", label: "Paid", hint: "Bonus transferred" },
  {
    value: "expired",
    label: "Expired",
    hint: "Window closed with no qualifying sale",
  },
  { value: "failed", label: "Failed", hint: "Payout hit the attempt cap" },
  {
    value: "not_started",
    label: "Not started",
    hint: "Track exists but has not been started",
  },
  {
    value: "empty",
    label: "Empty",
    hint: "No activation bonus track at all — never enrolled",
  },
];

const CREATED_RANGE_OPTIONS: { value: CreatedRangePreset; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "custom", label: "Custom range" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function truncate(value: string | null, max: number): string {
  if (!value) return "—";
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export function ActivationBonusView() {
  const navigate = useNavigate();
  const [state, bloc] = useStateMachine(
    () => new ActivationBonusBloc(new ActivationBonusRepository()),
  );

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  const customRange =
    state.createdRange.preset === "custom" ? state.createdRange.range : null;
  const rangeInvalid = isCreatedRangeInvalid(state.createdRange);

  async function handleRetry(businessId: string) {
    try {
      const result = await bloc.retryPayout(businessId);
      if (result.status === "succeeded") {
        toast.success("Retry succeeded — payout complete");
      } else if (result.status === "still_failed") {
        toast(
          `Retry triggered — still failed${result.message ? `: ${result.message}` : ""}`,
          {
            icon: "⚠️",
            style: {
              background: "#fef3c7",
              color: "#92400e",
              border: "1px solid #fde68a",
            },
          },
        );
      } else if (result.status === "cap_reached") {
        toast.error("Attempt cap reached (10/10) — no further retries");
      } else if (result.status === "ineligible") {
        toast.error(
          `Ineligible for retry${result.message ? `: ${result.message}` : ""}`,
        );
      } else {
        toast.error(
          `Retry error${result.message ? `: ${result.message}` : ""}`,
        );
      }
      void bloc.load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Retry failed");
    }
  }

  const columns: ColumnDef<ActivationBonusCampaignRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "email",
        header: "Merchant email",
        cell: ({ row }) => {
          const email = row.original.email;
          const businessId = row.original.businessId;
          if (email && businessId) {
            return (
              <button
                className="text-blue-600 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    ROUTES.MERCHANT_DETAIL.replace(":businessId", businessId),
                  );
                }}
              >
                {email}
              </button>
            );
          }
          return <span>{email ?? "—"}</span>;
        },
      },
      {
        accessorKey: "businessName",
        header: "Business name",
        cell: ({ row }) => (
          <span className="text-foreground text-sm">
            {row.original.businessName ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "activationBonusStage",
        header: "Stage",
        cell: ({ row }) => {
          const stage = row.original.activationBonusStage;
          if (!stage) return <span className="text-subtle-foreground">—</span>;
          return <StageBadge stage={stage} hasError={stage === "failed"} />;
        },
      },
      {
        accessorKey: "accountCreatedAt",
        header: "Account created",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {formatDate(row.original.accountCreatedAt)}
          </span>
        ),
      },
      {
        accessorKey: "startedAt",
        header: "Started at",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {formatDate(row.original.startedAt)}
          </span>
        ),
      },
      {
        accessorKey: "qualifyingTransactionId",
        header: "Qualifying tx",
        cell: ({ row }) => (
          <span
            className="text-muted-foreground font-mono text-xs"
            title={row.original.qualifyingTransactionId ?? undefined}
          >
            {truncate(row.original.qualifyingTransactionId, 16)}
          </span>
        ),
      },
      {
        accessorKey: "payoutStatus",
        header: "Payout status",
        cell: ({ row }) => {
          const { payoutStatus, attemptCount } = row.original;
          if (!payoutStatus)
            return <span className="text-subtle-foreground">—</span>;
          const label =
            payoutStatus === "failed"
              ? `failed (${attemptCount}/10)`
              : payoutStatus;
          return (
            <span
              className={
                payoutStatus === "succeeded"
                  ? "text-sm text-green-700"
                  : payoutStatus === "failed"
                    ? "text-sm text-red-700"
                    : "text-sm text-blue-700"
              }
            >
              {label}
            </span>
          );
        },
      },
      {
        accessorKey: "lastErrorMessage",
        header: "Last error",
        cell: ({ row }) => {
          const msg = row.original.lastErrorMessage;
          if (!msg) return <span className="text-subtle-foreground">—</span>;
          return (
            <span
              className="text-muted-foreground cursor-help text-xs"
              title={msg}
            >
              {truncate(msg, 40)}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const { businessId, payoutStatus, attemptCount } = row.original;
          const canRetry = payoutStatus === "failed" && attemptCount < 10;
          const isRetrying = state.retrying.has(businessId);

          return (
            <Button
              size="sm"
              variant="outline"
              disabled={!canRetry || isRetrying}
              onClick={(e) => {
                e.stopPropagation();
                void handleRetry(businessId);
              }}
            >
              {isRetrying ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Retry payout
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, state.retrying],
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-foreground text-2xl font-bold">Activation Bonus</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Merchants with an activation bonus campaign in flight, joined to their
          lifecycle track stage. Two chips surface merchants who are not being
          paid: <strong>Empty</strong> for those who never enrolled at all, and{" "}
          <strong>Not started</strong> for those whose track exists but never
          left the starting stage.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STAGE_OPTIONS.map((opt) => {
          const active = state.stageFilter.includes(opt.value);
          return (
            <button
              key={opt.value}
              title={opt.hint}
              onClick={() => {
                const next = active
                  ? state.stageFilter.filter((s) => s !== opt.value)
                  : [...state.stageFilter, opt.value];
                void bloc.setStageFilter(next);
              }}
              className={[
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                active
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-border bg-card text-muted-foreground hover:bg-muted",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
        {state.stageFilter.length > 0 && (
          <button
            onClick={() => void bloc.setStageFilter([])}
            className="text-muted-foreground hover:text-foreground rounded-full px-3 py-1 text-xs font-medium"
          >
            Clear
          </button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs font-medium">
          Account created
        </span>
        <Select
          value={state.createdRange.preset}
          onValueChange={(value) => {
            const preset = value as CreatedRangePreset;
            void bloc.setCreatedRange(
              preset === "custom"
                ? { preset, range: { from: null, to: null } }
                : { preset },
            );
          }}
        >
          <SelectTrigger className="h-8 w-[160px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CREATED_RANGE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {customRange && (
          <>
            <Input
              type="date"
              aria-label="Account created from"
              className="h-8 w-[150px] text-sm"
              value={customRange.from ?? ""}
              onChange={(e) => {
                void bloc.setCreatedRange({
                  preset: "custom",
                  range: { ...customRange, from: e.target.value || null },
                });
              }}
            />
            <span className="text-subtle-foreground text-xs">to</span>
            <Input
              type="date"
              aria-label="Account created to"
              className="h-8 w-[150px] text-sm"
              value={customRange.to ?? ""}
              onChange={(e) => {
                void bloc.setCreatedRange({
                  preset: "custom",
                  range: { ...customRange, to: e.target.value || null },
                });
              }}
            />
          </>
        )}

        {rangeInvalid && (
          <span className="text-xs font-medium text-red-600">
            Start date is after the end date — no merchant can match.
          </span>
        )}
      </div>

      {state.status === "loading" && state.items.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
        </div>
      ) : state.status === "error" && state.items.length === 0 ? (
        <div className="bg-card rounded-lg border px-4 py-8 text-center text-sm text-red-500">
          Failed to load activation bonus campaigns: {state.error}
        </div>
      ) : (
        <>
          {state.status === "error" && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
              Failed to load more campaigns: {state.error}
            </div>
          )}
          <DataTable
            columns={columns}
            data={state.items}
            onRowClick={(row) => {
              navigate(
                ROUTES.MERCHANT_DETAIL.replace(":businessId", row.businessId),
              );
            }}
          />
          {state.nextToken && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  void bloc.loadMore();
                }}
                disabled={state.status === "loading"}
              >
                {state.status === "loading" && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
